import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Spinner, Alert, Badge, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Command Center version of Upgrade Paths
 * Manage how customers move through your product ladder
 */
function CCUpgradePaths() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upgradePaths, setUpgradePaths] = useState([]);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPath, setEditingPath] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch('/api/upgrade-paths/get', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUpgradePaths(data.upgrade_paths || []);
        setProducts(data.products || []);
      }
    } catch (err) {
      setError('Failed to load upgrade paths');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingPath({
      from_product_id: '',
      to_product_id: '',
      conversion_rate: 10,
      time_value: 30,
      time_unit: 'days'
    });
    setShowEditModal(true);
  };

  const openEditModal = (path) => {
    setEditingPath({ ...path });
    setShowEditModal(true);
  };

  const savePath = async () => {
    if (!editingPath.from_product_id || !editingPath.to_product_id) {
      setError('Please select both products');
      return;
    }

    if (editingPath.from_product_id === editingPath.to_product_id) {
      setError('From and To products must be different');
      return;
    }

    setSaving(true);
    try {
      let updatedPaths;
      if (editingPath.id) {
        // Update existing path
        updatedPaths = upgradePaths.map(p =>
          p.id === editingPath.id ? editingPath : p
        );
      } else {
        // Add new path
        updatedPaths = [...upgradePaths, { ...editingPath, id: Date.now() }];
      }

      const response = await fetch('/api/upgrade-paths/save', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upgrade_paths: updatedPaths })
      });

      if (response.ok) {
        await fetchData(); // Refresh to get server-assigned IDs
        setShowEditModal(false);
        setSuccess('Upgrade path saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to save upgrade path');
      }
    } catch (err) {
      setError('Error saving upgrade path');
    } finally {
      setSaving(false);
    }
  };

  const deletePath = async (pathId) => {
    if (!window.confirm('Are you sure you want to delete this upgrade path?')) {
      return;
    }

    try {
      const updatedPaths = upgradePaths.filter(p => p.id !== pathId);

      const response = await fetch('/api/upgrade-paths/save', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upgrade_paths: updatedPaths })
      });

      if (response.ok) {
        setUpgradePaths(updatedPaths);
        setSuccess('Upgrade path deleted');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Error deleting upgrade path');
    }
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Unknown';
  };

  const formatTime = (value, unit) => {
    if (!value) return '-';
    return `${value} ${unit}`;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading upgrade paths...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2"
            onClick={() => navigate('/command-center/products')}
          >
            ← Back to Products
          </Button>
          <h1 className="mb-1">Upgrade Paths</h1>
          <p className="text-muted mb-0">
            Define how customers move through your product ladder. Used for funnel sales pages and post-purchase email sequences.
          </p>
        </div>
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={openAddModal}
          disabled={products.length < 2}
        >
          + Add Upgrade Path
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* No products warning */}
      {products.length < 2 && (
        <Alert variant="warning">
          You need at least 2 products to create upgrade paths.
          <Button
            variant="link"
            className="p-0 ms-2"
            onClick={() => navigate('/command-center/products/catalog')}
          >
            Add products →
          </Button>
        </Alert>
      )}

      {/* Upgrade Paths Table */}
      {upgradePaths.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
            <h4>No Upgrade Paths Defined</h4>
            <p className="text-muted mb-4">
              Upgrade paths define how customers move from one product to the next in your value ladder.
            </p>
            {products.length >= 2 && (
              <Button
                variant="dark"
                style={{ backgroundColor: colors.greyDarkest }}
                onClick={openAddModal}
              >
                Create Your First Path
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Table responsive hover className="mb-0">
            <thead style={{ backgroundColor: colors.greyLightest }}>
              <tr>
                <th>From Product</th>
                <th className="text-center">→</th>
                <th>To Product</th>
                <th className="text-center">Conversion Rate</th>
                <th className="text-center">Typical Time</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {upgradePaths.map(path => (
                <tr key={path.id}>
                  <td>
                    <strong>{path.from_product_name || getProductName(path.from_product_id)}</strong>
                  </td>
                  <td className="text-center text-muted">→</td>
                  <td>
                    <strong>{path.to_product_name || getProductName(path.to_product_id)}</strong>
                  </td>
                  <td className="text-center">
                    <Badge bg="primary">{path.conversion_rate}%</Badge>
                  </td>
                  <td className="text-center text-muted">
                    {formatTime(path.time_value, path.time_unit)}
                  </td>
                  <td className="text-center">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => openEditModal(path)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => deletePath(path.id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Visual Flow - Always show if there are products */}
      {products.length > 0 && (
        <Card className="mt-4 shadow-sm">
          <Card.Header style={{ backgroundColor: colors.greyLightest }}>
            <strong>Customer Journey Overview</strong>
            {upgradePaths.length === 0 && products.length > 1 && (
              <span className="text-muted ms-2" style={{ fontWeight: 'normal', fontSize: '0.85rem' }}>
                (Add upgrade paths to connect products)
              </span>
            )}
          </Card.Header>
          <Card.Body>
            {(() => {
              // Find products that are connected via paths
              const connectedProducts = new Set();
              upgradePaths.forEach(path => {
                connectedProducts.add(path.from_product_id);
                connectedProducts.add(path.to_product_id);
              });

              // Find orphan products (not in any path)
              const orphanProducts = products.filter(p => !connectedProducts.has(p.id));

              const getProduct = (id) => products.find(p => p.id === id);

              return (
                <div>
                  {/* Show each defined path */}
                  {upgradePaths.length > 0 && (
                    <div className="mb-4">
                      {upgradePaths.map((path, index) => {
                        const fromProduct = getProduct(path.from_product_id);
                        const toProduct = getProduct(path.to_product_id);
                        if (!fromProduct || !toProduct) return null;

                        // Alternate row colors for visual distinction
                        const rowBg = index % 2 === 0 ? '#f8f9fa' : '#ffffff';

                        return (
                          <div
                            key={path.id}
                            className="d-flex align-items-center gap-3 p-3 rounded mb-2"
                            style={{
                              backgroundColor: rowBg,
                              border: '1px solid #e9ecef'
                            }}
                          >
                            <div className="text-muted" style={{ fontSize: '0.75rem', minWidth: '20px' }}>
                              {index + 1}.
                            </div>
                            <div
                              className="text-center p-2 rounded"
                              style={{
                                backgroundColor: '#e8f5e9',
                                border: '2px solid #4caf50',
                                minWidth: '100px'
                              }}
                            >
                              <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{fromProduct.name}</div>
                              <div className="small text-muted">${fromProduct.sale_price || fromProduct.price || 0}</div>
                            </div>
                            <div className="text-center" style={{ color: '#4caf50' }}>
                              <div style={{ fontSize: '1.5rem' }}>→</div>
                              <div style={{ fontSize: '0.75rem', fontWeight: '600' }}>{path.conversion_rate}%</div>
                            </div>
                            <div
                              className="text-center p-2 rounded"
                              style={{
                                backgroundColor: '#e8f5e9',
                                border: '2px solid #4caf50',
                                minWidth: '100px'
                              }}
                            >
                              <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{toProduct.name}</div>
                              <div className="small text-muted">${toProduct.sale_price || toProduct.price || 0}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Orphan products (not connected to any path) */}
                  {orphanProducts.length > 0 && (
                    <div>
                      {upgradePaths.length > 0 && (
                        <div className="text-muted small mb-2">Not yet connected:</div>
                      )}
                      <div className="d-flex flex-wrap gap-2">
                        {orphanProducts.map(product => (
                          <div
                            key={product.id}
                            className="text-center p-2 rounded"
                            style={{
                              backgroundColor: '#f5f5f5',
                              border: '2px dashed #ccc',
                              minWidth: '100px',
                              opacity: 0.7
                            }}
                          >
                            <div style={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{product.name}</div>
                            <div className="small text-muted">${product.sale_price || product.price || 0}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </Card.Body>
        </Card>
      )}

      {/* Edit/Add Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>{editingPath?.id ? 'Edit' : 'Add'} Upgrade Path</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingPath && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label><strong>From Product</strong></Form.Label>
                <Form.Select
                  value={editingPath.from_product_id || ''}
                  onChange={(e) => setEditingPath({ ...editingPath, from_product_id: parseInt(e.target.value) })}
                >
                  <option value="">Select starting product...</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} (${p.price})</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>To Product</strong></Form.Label>
                <Form.Select
                  value={editingPath.to_product_id || ''}
                  onChange={(e) => setEditingPath({ ...editingPath, to_product_id: parseInt(e.target.value) })}
                >
                  <option value="">Select destination product...</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} (${p.price})</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Conversion Rate (%)</strong></Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={editingPath.conversion_rate || ''}
                  onChange={(e) => setEditingPath({ ...editingPath, conversion_rate: parseFloat(e.target.value) || 0 })}
                />
                <Form.Text className="text-muted">
                  What percentage of customers upgrade from one product to the next?
                </Form.Text>
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Typical Time</strong></Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={editingPath.time_value || ''}
                      onChange={(e) => setEditingPath({ ...editingPath, time_value: parseInt(e.target.value) || 0 })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Time Unit</strong></Form.Label>
                    <Form.Select
                      value={editingPath.time_unit || 'days'}
                      onChange={(e) => setEditingPath({ ...editingPath, time_unit: e.target.value })}
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={savePath}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Path'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CCUpgradePaths;
